import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { IkomatchCTA, trackIkomatchClick } from "./ikomatch-cta";
import { posthog } from "@/lib/posthog";

const SUPPRESSED_PREFIXES = [
  "/apply",
  "/apply-success",
  "/login",
  "/admin",
  "/tasks",
  "/investor",
  "/for-investors",
  "/grants",
];

const SESSION_TIMES_KEY = "deckscore_ikomatch_exit_times";
const MAX_PER_SESSION = 2;
const MIN_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
const GRACE_MS = 10 * 1000; // 10 seconds after mount

function isSuppressed(pathname: string) {
  return SUPPRESSED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

function readShowTimes(): number[] {
  try {
    const raw = sessionStorage.getItem(SESSION_TIMES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((n) => typeof n === "number") : [];
  } catch {
    return [];
  }
}

function writeShowTimes(times: number[]) {
  try {
    sessionStorage.setItem(SESSION_TIMES_KEY, JSON.stringify(times));
  } catch {
    /* ignore */
  }
}

export function IkomatchExitIntent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isSuppressed(pathname)) return;

    // desktop only
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (isMobile) return;

    const mountedAt = Date.now();

    const canShow = () => {
      if (Date.now() - mountedAt < GRACE_MS) return false;
      const times = readShowTimes();
      if (times.length >= MAX_PER_SESSION) return false;
      const last = times[times.length - 1];
      if (typeof last === "number" && Date.now() - last < MIN_INTERVAL_MS) return false;
      return true;
    };

    const onMouseOut = (e: MouseEvent) => {
      // Must be a genuine viewport exit at the top edge:
      // pointer left the document (no relatedTarget) AND crossed the top.
      if (e.relatedTarget !== null) return;
      if (e.clientY > 0) return;
      if (!canShow()) return;

      const times = readShowTimes();
      times.push(Date.now());
      writeShowTimes(times);

      setOpen(true);
      try {
        posthog.capture("ikomatch_exit_intent_shown");
      } catch {
        /* non-fatal */
      }

      if (times.length >= MAX_PER_SESSION) {
        document.removeEventListener("mouseout", onMouseOut);
      }
    };

    document.addEventListener("mouseout", onMouseOut);
    return () => document.removeEventListener("mouseout", onMouseOut);
  }, [pathname]);


  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4"
      onClick={() => setOpen(false)}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-[440px] rounded-2xl border border-line bg-surface p-7 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-script text-accent text-[22px] leading-none">before you go -</p>
        <h2 className="mt-2 font-display text-[24px] font-semibold text-ink">
          Want investors reached for you?
        </h2>
        <p className="mt-3 text-[15px] text-ink-secondary leading-relaxed">
          ikomatch sends 10 personalized investor emails in ~20 minutes, over WhatsApp.
          Every message is hand-written for that specific investor.
        </p>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-full border border-line bg-surface px-4 py-2.5 text-[13.5px] font-medium text-ink-secondary hover:text-ink"
          >
            No thanks
          </button>
          <IkomatchCTA
            placement="exit_intent"
            variant="button"
            onClick={() => setOpen(false)}
          >
            Start on WhatsApp →
          </IkomatchCTA>
        </div>
      </div>
    </div>
  );
}

// re-export helper if callers ever need it
export { trackIkomatchClick };
